import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { Check, ChevronDown, ChevronRight, Clock, Info, X } from 'lucide-react';
import { Fragment, useState } from 'react';

import type {
  EnrollmentSupport,
  PayerEnrollmentGroup,
  UnlinkedInsurance,
} from '../../services/clinicsApi';
import { PayerDetailsDrawer } from './PayerDetailsDrawer';
import { SupportIcon } from './SupportIcon';
import {
  PAYER_TRANSACTION_LABELS,
  PAYER_STATUS_LABELS,
  agencyTitle,
  markLabel,
  linesInView,
  payerStatus,
  transactionStates,
  type PayerStatus as PayerStatusValue,
  type PayerTransaction,
  type SummaryTone,
} from './payerEnrollments';

interface PayerEnrollmentsTableProps {
  payers: PayerEnrollmentGroup[];
  noPayerInsurances: UnlinkedInsurance[];
  clinicIds: string[];
  /** The transaction columns to show, in order. */
  transactions: PayerTransaction[];
  /** The payer shown beside the table. */
  selectedKey?: string | null;
  /** Show this payer beside the table. */
  onSelect?: (payerKey: string) => void;
}

// Bold so the status reads as the row's answer, in the ticks' colours: red to
// act on, green done, grey not live yet.
const STATUS_CLASSES: Record<PayerStatusValue, string> = {
  needs_action: 'font-medium text-red-600',
  waiting: 'font-medium text-muted-foreground',
  not_started: 'font-medium text-muted-foreground',
  enrolled: 'font-medium text-green-700',
  none_needed: 'font-medium text-muted-foreground',
};

/** The payer's status after its ID. */
function PayerStatus({
  payer,
  clinicIds,
  transactions,
}: {
  payer: PayerEnrollmentGroup;
  clinicIds: string[];
  transactions: PayerTransaction[];
}) {
  const status = payerStatus(payer, clinicIds, transactions);
  if (!status) return null;
  return (
    <>
      {' · '}
      <span className={STATUS_CLASSES[status]}>
        {PAYER_STATUS_LABELS[status]}
      </span>
    </>
  );
}

/**
 * A clinic line's mark: a green check when this clinic is done (live, or no
 * enrollment needed), a grey clock once submitted, a red cross while the agency
 * has something to do, nothing when the payer does not
 * support the transaction. The payer row carries the detail; this is the
 * at-a-glance answer per clinic.
 */
function ClinicDot({
  support,
  state,
}: {
  support?: EnrollmentSupport;
  state: SummaryTone | null;
}) {
  if (
    support === 'not_supported' ||
    (support !== 'enrollment_required' && !state)
  ) {
    return <span className='text-muted-foreground'>—</span>;
  }
  const done =
    support === 'enrollment_not_required' ||
    state === 'enrolled' ||
    state === 'none_needed';
  const label = markLabel(support, state);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role='img'
          aria-label={label}
          className='inline-flex items-center justify-center'
        >
          {done ? (
            <Check className='h-3.5 w-3.5 text-green-600' strokeWidth={4} />
          ) : state === 'waiting' ? (
            <Clock
              className='h-3.5 w-3.5 text-muted-foreground'
              strokeWidth={3}
            />
          ) : (
            <X className='h-3.5 w-3.5 text-red-600' strokeWidth={4} />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * One row per payer: what the payer supports per transaction, with the tick
 * coloured by where the clinics in view stand. A row opens to one line per
 * clinic with that clinic's own ticks; a click shows the payer and its steps
 * beside the table.
 */
export function PayerEnrollmentsTable({
  payers,
  noPayerInsurances,
  clinicIds,
  transactions,
  selectedKey,
  onSelect,
}: PayerEnrollmentsTableProps) {
  const [showNoPayer, setShowNoPayer] = useState(false);
  const [details, setDetails] = useState<PayerEnrollmentGroup | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setOpen(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (payers.length === 0 && noPayerInsurances.length === 0) {
    return (
      <div className='rounded-md border py-8 text-center text-sm text-muted-foreground'>
        No payers match.
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className='overflow-x-auto rounded-md border'>
        <Table className='min-w-[520px] table-fixed'>
          <TableHeader>
            <TableRow>
              <TableHead>Payer</TableHead>
              {transactions.map(transaction => (
                <TableHead
                  key={transaction}
                  className='w-24 whitespace-nowrap text-center'
                >
                  {transaction === 'claims'
                    ? 'Claims (837I)'
                    : PAYER_TRANSACTION_LABELS[transaction]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payers.map(payer => {
              const title = agencyTitle(payer, clinicIds);
              const states = new Map(
                transactionStates(payer, clinicIds, transactions).map(item => [
                  item.transaction,
                  item.state,
                ])
              );
              const isOpen = open.has(payer.key);
              return (
                <Fragment key={payer.key}>
                  <TableRow
                    onClick={() => onSelect?.(payer.key)}
                    aria-selected={selectedKey === payer.key}
                    className={`cursor-pointer ${
                      selectedKey === payer.key ? 'bg-muted hover:bg-muted' : ''
                    }`}
                  >
                    <TableCell>
                      {/* A click anywhere shows the payer beside the table;
                          the chevron alone opens its clinic lines. */}
                      <div className='flex items-center gap-2'>
                        <button
                          type='button'
                          aria-expanded={isOpen}
                          aria-label={isOpen ? 'Hide clinics' : 'Show clinics'}
                          onClick={event => {
                            event.stopPropagation();
                            toggle(payer.key);
                          }}
                          className='shrink-0 rounded text-muted-foreground hover:text-foreground'
                        >
                          {isOpen ? (
                            <ChevronDown className='h-5 w-5' />
                          ) : (
                            <ChevronRight className='h-5 w-5' />
                          )}
                        </button>
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-center gap-1.5'>
                            {/* One line: a long name ends in "…", and the count
                              of other names stays whole after it. */}
                            <button
                              type='button'
                              onClick={event => {
                                event.stopPropagation();
                                onSelect?.(payer.key);
                              }}
                              className='flex max-w-full text-left font-medium'
                              title={
                                title
                                  ? `${title.all.join(', ')} · paid by ${payer.name}`
                                  : payer.name
                              }
                            >
                              <span className='truncate'>
                                {title?.all[0] ?? payer.name}
                              </span>
                              {title && title.all.length > 1 && (
                                <span className='shrink-0 whitespace-nowrap'>
                                  &nbsp;+{title.all.length - 1} more
                                </span>
                              )}
                            </button>
                            {/* Reference only, so small and quiet: the steps
                              are what the row opens. */}
                            <button
                              type='button'
                              aria-label='Payer details'
                              title='Payer details'
                              onClick={event => {
                                event.stopPropagation();
                                setDetails(payer);
                              }}
                              className='shrink-0 rounded text-muted-foreground hover:text-foreground'
                            >
                              <Info className='h-4 w-4' />
                            </button>
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            {payer.primary_payer_id ?? 'No payer ID'}
                            <PayerStatus
                              payer={payer}
                              clinicIds={clinicIds}
                              transactions={transactions}
                            />
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    {transactions.map(transaction => (
                      <TableCell key={transaction} className='text-center'>
                        <SupportIcon
                          support={payer[transaction]?.support}
                          state={states.get(transaction) ?? null}
                          size='large'
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                  {isOpen &&
                    linesInView(payer, clinicIds).map(line => {
                      const lineStates = new Map(
                        transactionStates(
                          payer,
                          [line.agency_clinic_id],
                          transactions
                        ).map(item => [item.transaction, item.state])
                      );
                      return (
                        <TableRow
                          key={`${payer.key}-${line.agency_clinic_id}`}
                          className='bg-muted/30 hover:bg-muted/30'
                        >
                          <TableCell className='pl-12 text-xs'>
                            {line.clinic_name ?? 'Unnamed clinic'}
                          </TableCell>
                          {transactions.map(transaction => (
                            <TableCell
                              key={transaction}
                              className='text-center'
                            >
                              <ClinicDot
                                support={payer[transaction]?.support}
                                state={lineStates.get(transaction) ?? null}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                </Fragment>
              );
            })}
            {noPayerInsurances.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={transactions.length + 1}>
                    <button
                      type='button'
                      onClick={() => setShowNoPayer(value => !value)}
                      aria-expanded={showNoPayer}
                      className='flex items-center gap-2 text-muted-foreground'
                    >
                      {showNoPayer ? (
                        <ChevronDown className='h-4 w-4' />
                      ) : (
                        <ChevronRight className='h-4 w-4' />
                      )}
                      No payer ID ({noPayerInsurances.length})
                    </button>
                  </TableCell>
                </TableRow>
                {showNoPayer &&
                  noPayerInsurances.map(insurance => (
                    <TableRow
                      key={insurance.id}
                      className='hover:bg-transparent'
                    >
                      <TableCell
                        colSpan={transactions.length + 1}
                        className='pl-10 text-sm'
                      >
                        {insurance.name}
                        {insurance.clinic_name && (
                          <span className='text-muted-foreground'>
                            {' '}
                            · {insurance.clinic_name}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </>
            )}
          </TableBody>
        </Table>
        <PayerDetailsDrawer
          payer={details}
          clinicIds={clinicIds}
          onClose={() => setDetails(null)}
        />
      </div>
    </TooltipProvider>
  );
}
